import { useMemo, useState } from "react";
import { signOut } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import {
  PLATFORM_DISCLAIMER_ITEMS,
  PLATFORM_DISCLAIMER_VERSION,
} from "../config/platformDisclaimer";
import { MAIL_API_BASE } from "../config/api";
import "./css/DisclaimerAcceptancePage.css";

const API_BASE = MAIL_API_BASE;

function getRedirectPath(locationState) {
  const fromPath = locationState?.from?.pathname || "/clients";
  if (!fromPath || fromPath === "/accept-disclaimer" || fromPath === "/login") {
    return "/clients";
  }
  return fromPath;
}

export default function DisclaimerAcceptancePage() {
  const nav = useNavigate();
  const location = useLocation();
  const { refreshAuthProfile } = useAuth();

  const [checked, setChecked] = useState(() =>
    PLATFORM_DISCLAIMER_ITEMS.map(() => false)
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const allChecked = useMemo(() => checked.every(Boolean), [checked]);
  const redirectPath = getRedirectPath(location.state);

  function toggleItem(index) {
    setChecked((prev) =>
      prev.map((value, i) => (i === index ? !value : value))
    );
    setErr("");
  }

  async function handleAccept(e) {
    e.preventDefault();
    if (!allChecked || saving) return;

    try {
      setSaving(true);
      setErr("");

      const token = await auth.currentUser?.getIdToken(true);
      if (!token) throw new Error("Your session expired. Please sign in again.");

      const resp = await fetch(`${API_BASE}/auth/accept-platform-disclaimer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ version: PLATFORM_DISCLAIMER_VERSION }),
      });

      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json?.ok === false) {
        throw new Error(json?.error || "Failed to record acceptance.");
      }

      await refreshAuthProfile();
      nav(redirectPath, { replace: true });
    } catch (e2) {
      setErr(e2?.message || "Failed to record acceptance.");
    } finally {
      setSaving(false);
    }
  }

  async function handleLogout() {
    await signOut(auth).catch(() => {});
    nav("/login", { replace: true });
  }

  return (
    <div className="disclaimerPage">
      <main className="disclaimerShell">
        <section className="disclaimerCard" aria-labelledby="disclaimerTitle">
          <div className="disclaimerHeader">
            <div className="disclaimerEyebrow">Required acknowledgement</div>
            <h1 id="disclaimerTitle" className="disclaimerTitle">
              Before you continue
            </h1>
            <p className="disclaimerLead">
              Please review and accept these acknowledgements to use myKYC
            </p>
          </div>

          <form onSubmit={handleAccept} className="disclaimerForm">
            <div className="disclaimerChecklist">
              {PLATFORM_DISCLAIMER_ITEMS.map((item, index) => (
                <label
                  key={item}
                  className={`disclaimerCheckRow${checked[index] ? " isChecked" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={checked[index]}
                    onChange={() => toggleItem(index)}
                  />
                  <span className="disclaimerCheckBox" aria-hidden="true">
                    <i className="bi bi-check-lg" />
                  </span>
                  <span className="disclaimerCheckText">{item}</span>
                </label>
              ))}
            </div>

            {err ? <div className="disclaimerError">{err}</div> : null}

            <div className="disclaimerActions">
              <button
                type="button"
                className="disclaimerLogoutBtn"
                onClick={handleLogout}
                disabled={saving}
              >
                Sign out
              </button>
              <button
                type="submit"
                className={`disclaimerAcceptBtn${!allChecked ? " isIncomplete" : ""}`}
                disabled={!allChecked || saving}
              >
                {saving ? "Recording acceptance..." : "Accept and continue"}
              </button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}
